import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH ====================

export const login = async (email, password, recaptchaToken = null) => {
  const response = await api.post('/api/auth/login', { email, password, recaptchaToken });
  return response.data;
};

export const requestMagicLink = async (email, recaptchaToken = null) => {
  const response = await api.post('/api/auth/magic-link', { email, recaptchaToken });
  return response.data;
};

export const verifyMagicCode = async (email, code) => {
  const response = await api.post(`/api/auth/verify-code?email=${encodeURIComponent(email)}&code=${code}`);
  return response.data;
};

export const verifyMagicLink = async (token) => {
  const response = await api.get(`/api/auth/magic-link/verify?token=${token}`);
  return response.data;
};

export const getLoginStatus = async (email) => {
  const response = await api.get(`/api/auth/login-status/${encodeURIComponent(email)}`);
  return response.data;
};

export const register = async (name, email, password, phone = '') => {
  const response = await api.post('/api/auth/register', { name, email, password, phone });
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/api/auth/me');
  return response.data;
};

export const changePassword = async (oldPassword, newPassword) => {
  const response = await api.put('/api/auth/change-password', { old_password: oldPassword, new_password: newPassword });
  return response.data;
};

// ==================== REFERIDOS ====================

export const createReferidoLead = async (leadData) => {
  const response = await api.post('/api/referidos/leads', leadData);
  return response.data;
};

export const getMyReferidoLeads = async () => {
  const response = await api.get('/api/referidos/my-leads');
  return response.data;
};

export const getAllReferidoLeads = async () => {
  const response = await api.get('/api/referidos/all-leads');
  return response.data;
};

export const getAllReferidoLeadsPaginated = async (page = 1, limit = 20, search = '') => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.append('search', search);
  const response = await api.get(`/api/referidos/all-leads-paginated?${params}`);
  return response.data;
};

export const unblockUser = async (userId) => {
  const response = await api.post(`/api/users/${userId}/unblock`);
  return response.data;
};

export const updateLeadCommission = async (leadId, commissionAmount, commissionStatus) => {
  const response = await api.put(`/api/referidos/leads/${leadId}/commission`, { 
    commission_amount: commissionAmount, 
    commission_status: commissionStatus 
  });
  return response.data;
};

// ==================== REFERIDOR DOCUMENTS ====================

export const getMyReferidorDocuments = async () => {
  const response = await api.get('/api/referidos/my-documents');
  return response.data;
};

export const uploadReferidorDocument = async (documentType, fileName, fileData, fileType) => {
  const response = await api.post('/api/referidos/my-documents', {
    documentType,
    fileName,
    fileData,
    fileType
  });
  return response.data;
};

export const deleteReferidorDocument = async (documentType) => {
  const response = await api.delete(`/api/referidos/my-documents/${documentType}`);
  return response.data;
};

// ==================== CONTACT INFO ====================

export const getMyContactInfo = async () => {
  const response = await api.get('/api/users/me/contact-info');
  return response.data;
};

export const updateMyContactInfo = async (contactInfo) => {
  const response = await api.put('/api/users/me/contact-info', contactInfo);
  return response.data;
};

// ==================== TOQUES (CONTACT TRACKING) ====================

export const addToque = async (referidorId, toque) => {
  const response = await api.post(`/api/referidores/${referidorId}/toques`, toque);
  return response.data;
};

export const updateToque = async (referidorId, toqueId, toque) => {
  const response = await api.put(`/api/referidores/${referidorId}/toques/${toqueId}`, toque);
  return response.data;
};

export const deleteToque = async (referidorId, toqueId) => {
  const response = await api.delete(`/api/referidores/${referidorId}/toques/${toqueId}`);
  return response.data;
};

export const updateProfileTag = async (referidorId, profileTag) => {
  const response = await api.put(`/api/referidores/${referidorId}/profile-tag`, { profileTag });
  return response.data;
};

export const updateOptimalServices = async (referidorId, serviceIds) => {
  const response = await api.put(`/api/referidores/${referidorId}/optimal-services`, { serviceIds });
  return response.data;
};

// ==================== REFERIDORES MANAGEMENT ====================
export const getReferidores = async () => {
  const response = await api.get('/api/referidores');
  return response.data;
};

export const getReferidorDetail = async (referidorId) => {
  const response = await api.get(`/api/referidores/${referidorId}`);
  return response.data;
};

export const updateReferidorRating = async (referidorId, rating) => {
  const response = await api.put(`/api/referidores/${referidorId}/rating`, { rating });
  return response.data;
};

export const assignReferidorToLead = async (leadId, referidorId) => {
  const response = await api.put(`/api/leads/${leadId}/referidor`, { referidor_id: referidorId });
  return response.data;
};


// ==================== LEADS ====================

export const getLeads = async (page = 1, limit = 50, pipelineId = null, search = null, dateFrom = null, dateTo = null, stageId = null, tag = null, assignedTo = null) => {
  const params = new URLSearchParams({ page, limit });
  if (pipelineId) params.append('pipeline_id', pipelineId);
  if (stageId) params.append('stage_id', stageId);
  if (tag) params.append('tag', tag);
  if (assignedTo) params.append('assigned_to', assignedTo);
  if (search) params.append('search', search);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  
  const response = await api.get(`/api/leads?${params}`);
  return response.data;
};

// Alias for getLeads with pagination support
export const getLeadsPaginated = getLeads;

export const getLeadsStats = async () => {
  const response = await api.get('/api/leads/stats');
  return response.data;
};

export const getTraffickerLeads = async () => {
  const response = await api.get('/api/leads/trafficker');
  return response.data;
};

// Trafficker Behavior Tracking
export const getTraffickerCampaigns = async () => {
  const response = await api.get('/api/trafficker/campaigns');
  return response.data;
};

export const updateTraffickerCampaigns = async (campaigns) => {
  const response = await api.put('/api/trafficker/campaigns', { campaigns });
  return response.data;
};

export const getTraffickerWeeks = async (year) => {
  const url = year ? `/api/trafficker/weeks?year=${year}` : '/api/trafficker/weeks';
  const response = await api.get(url);
  return response.data;
};

export const createTraffickerWeek = async (data) => {
  const response = await api.post('/api/trafficker/weeks', data);
  return response.data;
};

export const updateTraffickerWeek = async (weekId, data) => {
  const response = await api.put(`/api/trafficker/weeks/${weekId}`, data);
  return response.data;
};

export const deleteTraffickerWeek = async (weekId) => {
  const response = await api.delete(`/api/trafficker/weeks/${weekId}`);
  return response.data;
};

export const getTraffickerWeekLeads = async (weekId, startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get(`/api/trafficker/weeks/${weekId}/leads`, { params });
  return response.data;
};

export const getTraffickerWeekStats = async (weekId, startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get(`/api/trafficker/weeks/${weekId}/stats`, { params });
  return response.data;
};

export const getMisCampanas = async () => {
  const response = await api.get('/api/campaigns/mis-campanas');
  return response.data;
};



export const getLeadsStatsByRating = async (dateRange = 'all', customFrom = null, customTo = null) => {
  let url = `/api/leads/stats/by-rating?date_range=${dateRange}`;
  if (dateRange === 'custom' && customFrom && customTo) {
    url += `&custom_from=${customFrom}&custom_to=${customTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getLeadsStatsByPeriod = async (period = 'month', customFrom = null, customTo = null) => {
  const params = new URLSearchParams({ period });
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  const response = await api.get(`/api/leads/stats/period?${params}`);
  return response.data;
};

export const getLeadsByPipeline = async (pipelineId, limitPerStage = 50) => {
  const response = await api.get(`/api/leads/by-pipeline-grouped/${pipelineId}?limit_per_stage=${limitPerStage}`);
  return response.data;
};

export const getLeadsByStage = async (pipelineId, stageId, skip = 0, limit = 10, startDate = null, endDate = null) => {
  const params = new URLSearchParams({ skip, limit });
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const response = await api.get(`/api/leads/by-stage/${pipelineId}/${stageId}?${params}`);
  return response.data;
};

export const searchLeads = async (pipelineId, query, limit = 5) => {
  const response = await api.get(`/api/leads/search/${pipelineId}?q=${encodeURIComponent(query)}&limit=${limit}`);
  return response.data;
};

export const getLead = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}`);
  return response.data.lead;
};

export const createLead = async (lead) => {
  const response = await api.post('/api/leads', lead);
  return response.data.lead;
};

export const updateLead = async (leadId, updates) => {
  const response = await api.put(`/api/leads/${leadId}`, updates);
  return response.data.lead;
};

export const deleteLead = async (leadId) => {
  await api.delete(`/api/leads/${leadId}`);
};

export const updateLeadRating = async (leadId, rating) => {
  const response = await api.put(`/api/leads/${leadId}/rating`, { rating });
  return response.data;
};

export const assignLead = async (leadId, assignedTo) => {
  const response = await api.put(`/api/leads/${leadId}/assign`, { assignedTo });
  return response.data;
};

export const getLeadFormHistory = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}/form-history`);
  return response.data;
};

export const consolidateLeadsByEmail = async (email) => {
  const response = await api.get(`/api/leads/consolidate/by-email?email=${encodeURIComponent(email)}`);
  return response.data;
};

// ==================== PIPELINES ====================

export const getPipelines = async () => {
  const response = await api.get('/api/pipelines');
  return response.data.pipelines || [];
};

export const createPipeline = async (pipeline) => {
  const response = await api.post('/api/pipelines', pipeline);
  return response.data.pipeline;
};

export const updatePipeline = async (pipelineId, updates) => {
  const response = await api.put(`/api/pipelines/${pipelineId}`, updates);
  return response.data.pipeline;
};

export const deletePipeline = async (pipelineId) => {
  await api.delete(`/api/pipelines/${pipelineId}`);
};

// ==================== FIELDS ====================

export const getFields = async () => {
  const response = await api.get('/api/fields');
  return response.data.fields || [];
};

export const createField = async (field) => {
  const response = await api.post('/api/fields', field);
  return response.data.field;
};

// ==================== ACTIVITIES ====================

export const getActivities = async (leadId = null) => {
  const url = leadId ? `/api/activities?leadId=${leadId}` : '/api/activities';
  const response = await api.get(url);
  return response.data.activities || [];
};

export const createActivity = async (activity) => {
  const response = await api.post('/api/activities', activity);
  return response.data.activity;
};

export const updateActivity = async (activityId, updates) => {
  const response = await api.put(`/api/activities/${activityId}`, updates);
  return response.data.activity;
};

export const deleteActivity = async (activityId) => {
  await api.delete(`/api/activities/${activityId}`);
};

// ==================== ALERTS ====================

export const getAlerts = async (leadId = null) => {
  const url = leadId ? `/api/alerts?leadId=${leadId}` : '/api/alerts';
  const response = await api.get(url);
  return response.data.alerts || [];
};

export const createAlert = async (alert) => {
  const response = await api.post('/api/alerts', alert);
  return response.data.alert;
};

export const updateAlert = async (alertId, updates) => {
  const response = await api.put(`/api/alerts/${alertId}`, updates);
  return response.data.alert;
};

export const deleteAlert = async (alertId) => {
  await api.delete(`/api/alerts/${alertId}`);
};

// ==================== MEETINGS ====================

export const getMeetings = async (leadId = null) => {
  const url = leadId ? `/api/meetings?leadId=${leadId}` : '/api/meetings';
  const response = await api.get(url);
  
  // Check if any meetings were deleted from calendar
  const deletedCount = response.data.deletedFromCalendar || 0;
  if (deletedCount > 0) {
    // Import toast dynamically to show notification
    const { toast } = await import('sonner');
    toast.info(`${deletedCount} reunión(es) eliminada(s) del calendario`);
  }
  
  return response.data.meetings || [];
};

export const createMeeting = async (meeting) => {
  const response = await api.post('/api/meetings', meeting);
  return response.data; // Returns {success, meeting, calendarSynced}
};

export const updateMeeting = async (meetingId, updates) => {
  const response = await api.put(`/api/meetings/${meetingId}`, updates);
  return response.data; // Returns {success, meeting, calendarUpdated}
};

export const deleteMeeting = async (meetingId) => {
  const response = await api.delete(`/api/meetings/${meetingId}`);
  return response.data; // Returns {success, calendarDeleted}
};

export const syncMeeting = async (meetingId) => {
  const response = await api.get(`/api/meetings/${meetingId}/sync`);
  return response.data; // Returns {meeting, synced} or {deleted, message}
};

export const syncAllMeetings = async () => {
  const response = await api.post('/api/meetings/sync-all');
  return response.data; // Returns {synced, deleted, total}
};

// ==================== ATTACHMENTS ====================

export const getAttachments = async (leadId = null) => {
  const url = leadId ? `/api/attachments?leadId=${leadId}` : '/api/attachments';
  const response = await api.get(url);
  return response.data.attachments || [];
};

export const createAttachment = async (attachment) => {
  const response = await api.post('/api/attachments', attachment);
  return response.data.attachment;
};

export const updateAttachment = async (attachmentId, updates) => {
  const response = await api.put(`/api/attachments/${attachmentId}`, updates);
  return response.data.attachment;
};

export const deleteAttachment = async (attachmentId) => {
  await api.delete(`/api/attachments/${attachmentId}`);
};

// ==================== FORM CAPTURE ====================

export const captureFormData = async (formId, pipelineId, data) => {
  const response = await api.post('/api/form-capture', {
    form_id: formId,
    pipeline_id: pipelineId,
    data: data
  });
  return response.data;
};

// ==================== ADMIN SETTINGS ====================

export const getSettings = async () => {
  try {
    const response = await api.get('/api/settings');
    return response.data.settings;
  } catch {
    return null;
  }
};

export const saveSettings = async (settings) => {
  const response = await api.post('/api/settings', settings);
  return response.data;
};

export const getEmailSettings = async () => {
  try {
    const response = await api.get('/api/settings/email');
    return response.data.settings;
  } catch {
    return null;
  }
};

export const saveEmailSettings = async (settings) => {
  const response = await api.post('/api/settings/email', settings);
  return response.data;
};

export const sendTestEmail = async (email) => {
  const response = await api.post('/api/settings/email/test', { email });
  return response.data;
};

export const sendDailyAlertReport = async () => {
  const response = await api.post('/api/alerts/send-daily-report');
  return response.data;
};

export const getAISettings = async () => {
  try {
    const response = await api.get('/api/settings/ai');
    return response.data.settings;
  } catch {
    return null;
  }
};

export const saveAISettings = async (settings) => {
  const response = await api.post('/api/settings/ai', settings);
  return response.data;
};

export const testAIConnection = async (provider) => {
  const response = await api.post('/api/settings/ai/test', { provider });
  return response.data;
};

// Statistics API
export const getPipelineStatistics = async (pipelineId, dateRange = 'year', dateFrom = '', dateTo = '') => {
  // If pipelineId is 'all', use the aggregated endpoint
  if (pipelineId === 'all') {
    let url = `/api/statistics/all/aggregated?date_range=${dateRange}`;
    if (dateRange === 'custom' && dateFrom && dateTo) {
      url += `&date_from=${dateFrom}&date_to=${dateTo}`;
    }
    const response = await api.get(url);
    return response.data;
  }
  
  let url = `/api/statistics/${pipelineId}?date_range=${dateRange}`;
  if (dateRange === 'custom' && dateFrom && dateTo) {
    url += `&date_from=${dateFrom}&date_to=${dateTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getPipelineStatisticsComparison = async (pipelineId, dateRange, comparisonType, dateFrom = '', dateTo = '') => {
  let url = `/api/statistics/${pipelineId}/comparison?date_range=${dateRange}&comparison_type=${comparisonType}`;
  if (dateRange === 'custom' && dateFrom && dateTo) {
    url += `&date_from=${dateFrom}&date_to=${dateTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getCommercialStatistics = async (dateRange = 'month', customFrom = null, customTo = null) => {
  let url = `/api/statistics/commercials?date_range=${dateRange}`;
  if (dateRange === 'custom' && customFrom && customTo) {
    url += `&custom_from=${customFrom}&custom_to=${customTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

// Services API
export const getServices = async () => {
  const response = await api.get('/api/services');
  return response.data;
};

export const getService = async (serviceId) => {
  const response = await api.get(`/api/services/${serviceId}`);
  return response.data;
};

export const createService = async (serviceData) => {
  const response = await api.post('/api/services', serviceData);
  return response.data;
};

export const updateService = async (serviceId, serviceData) => {
  const response = await api.put(`/api/services/${serviceId}`, serviceData);
  return response.data;
};

export const deleteService = async (serviceId) => {
  const response = await api.delete(`/api/services/${serviceId}`);
  return response.data;
};

// ==================== EMPRESAS (COMPANIES) ====================

export const getEmpresas = async (page = 1, limit = 50, search = null, status = null, startDate = null, endDate = null) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.append('search', search);
  if (status) params.append('status', status);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const response = await api.get(`/api/empresas?${params}`);
  return response.data;
};

export const getEmpresa = async (empresaId) => {
  const response = await api.get(`/api/empresas/${empresaId}`);
  return response.data;
};

export const searchEmpresas = async (query = '', limit = 10) => {
  const response = await api.get(`/api/empresas/search?query=${encodeURIComponent(query)}&limit=${limit}`);
  return response.data;
};

export const createEmpresa = async (empresaData) => {
  const response = await api.post('/api/empresas', empresaData);
  return response.data;
};

export const batchCreateEmpresas = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/api/empresas/batch', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const createEmpresaFromLead = async (leadId) => {
  const response = await api.post(`/api/empresas/from-lead/${leadId}`);
  return response.data;
};

export const updateEmpresa = async (empresaId, empresaData) => {
  const response = await api.put(`/api/empresas/${empresaId}`, empresaData);
  return response.data;
};

export const deleteEmpresa = async (empresaId) => {
  const response = await api.delete(`/api/empresas/${empresaId}`);
  return response.data;
};

export const addContactToEmpresa = async (empresaId, leadData) => {
  const response = await api.post(`/api/empresas/${empresaId}/contacts`, leadData);
  return response.data;
};

export const getEmpresaDeals = async (empresaId) => {
  const response = await api.get(`/api/empresas/${empresaId}/deals`);
  return response.data;
};

export const createDealFromLead = async (leadId, dealData) => {
  const response = await api.post(`/api/deals/from-lead/${leadId}`, dealData);
  return response.data;
};

// ==================== DEALS ====================

// Deals API
export const getDeals = async (page = 1, limit = 50, status = null, search = null) => {
  const params = new URLSearchParams({ page, limit });
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  const response = await api.get(`/api/deals?${params}`);
  return response.data;
};

export const getDeal = async (dealId) => {
  const response = await api.get(`/api/deals/${dealId}`);
  return response.data;
};

export const createDeal = async (dealData) => {
  const response = await api.post('/api/deals', dealData);
  return response.data;
};

export const updateDeal = async (dealId, dealData) => {
  const response = await api.put(`/api/deals/${dealId}`, dealData);
  return response.data;
};

export const deleteDeal = async (dealId) => {
  const response = await api.delete(`/api/deals/${dealId}`);
  return response.data;
};

export const getDealsMonthlyStats = async (dateRange = null, customFrom = null, customTo = null, pipelineId = null) => {
  const params = new URLSearchParams();
  if (dateRange) params.append('date_range', dateRange);
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  if (pipelineId && pipelineId !== 'all') params.append('pipeline_id', pipelineId);
  const response = await api.get(`/api/deals/stats/monthly?${params}`);
  return response.data;
};

export const getDealsStatsByPeriod = async (period = 'month', customFrom = null, customTo = null) => {
  const params = new URLSearchParams();
  params.append('period', period);
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  const response = await api.get(`/api/deals/stats/period?${params}`);
  return response.data;
};

export const getDealsStatsByService = async (dateRange = null, customFrom = null, customTo = null, pipelineId = null) => {
  const params = new URLSearchParams();
  if (dateRange) params.append('date_range', dateRange);
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  if (pipelineId && pipelineId !== 'all') params.append('pipeline_id', pipelineId);
  const response = await api.get(`/api/deals/stats/by-service?${params}`);
  return response.data;
};

// ==================== LEAD JOURNEY ====================
export const getLeadJourney = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}/journey`);
  return response.data;
};

// ==================== STATISTICS PATTERNS ====================
export const getBehaviorPatterns = async (dateRange = 'year', pipelineId = null, customFrom = null, customTo = null) => {
  const params = new URLSearchParams();
  params.append('date_range', dateRange);
  if (pipelineId && pipelineId !== 'all') params.append('pipeline_id', pipelineId);
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  const response = await api.get(`/api/statistics/patterns?${params}`);
  return response.data;
};

// ==================== LOSS REASON STATISTICS ====================
export const getLossReasonStatistics = async (dateRange = 'month', pipelineId = null, customFrom = null, customTo = null) => {
  const params = new URLSearchParams();
  params.append('date_range', dateRange);
  if (pipelineId && pipelineId !== 'all') params.append('pipeline_id', pipelineId);
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  const response = await api.get(`/api/statistics/loss-reasons?${params}`);
  return response.data;
};

// ==================== USERS ====================
export const getUsers = async () => {
  const response = await api.get('/api/users');
  return response.data;
};

export const getUser = async (userId) => {
  const response = await api.get(`/api/users/${userId}`);
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/api/users', userData);
  return response.data;
};

export const batchCreateUsers = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/api/users/batch', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/api/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/api/users/${userId}`);
  return response.data;
};

export const impersonateUser = async (userId) => {
  const response = await api.post(`/api/auth/impersonate/${userId}`);
  return response.data;
};

// Associate existing lead to empresa
export const associateLeadToEmpresa = async (empresaId, leadId) => {
  const response = await api.post(`/api/empresas/${empresaId}/associate-lead/${leadId}`);
  return response.data;
};

// Tracking settings
export const getTrackingSettings = async () => {
  const response = await api.get('/api/settings/tracking');
  return response.data;
};

export const getTrackingStatistics = async () => {
  const response = await api.get('/api/statistics/tracking');
  return response.data;
};

export default api;

// Bulk import leads
export const bulkImportLeads = async (leads) => {
  const response = await api.post('/api/leads/bulk-import', leads);
  return response.data;
};

// Get template info for bulk import
export const getLeadsTemplate = async () => {
  const response = await api.get('/api/leads/template-download');
  return response.data;
};

// Google Calendar integration
export const getCalendarAuthUrl = async (userId) => {
  const response = await api.get(`/api/oauth/calendar/auth-url?user_id=${userId}`);
  return response.data;
};

export const disconnectCalendar = async (userId) => {
  const response = await api.delete(`/api/oauth/calendar/disconnect/${userId}`);
  return response.data;
};

// ==================== GOOGLE ADS ====================

export const getGoogleAdsStatus = async () => {
  const response = await api.get('/api/google-ads/status');
  return response.data;
};

export const getGoogleAdsCampaigns = async (customerId = null) => {
  const params = customerId ? `?customer_id=${customerId}` : '';
  const response = await api.get(`/api/google-ads/campaigns${params}`);
  return response.data;
};

export const getGoogleAdsCampaignDetails = async (campaignId, customerId = null, dateRange = 'LAST_30_DAYS') => {
  const params = new URLSearchParams({ date_range: dateRange });
  if (customerId) params.append('customer_id', customerId);
  const response = await api.get(`/api/google-ads/campaigns/${campaignId}?${params}`);
  return response.data;
};

export const getGoogleAdsAccountSummary = async (customerId = null, dateRange = 'LAST_30_DAYS') => {
  const params = new URLSearchParams({ date_range: dateRange });
  if (customerId) params.append('customer_id', customerId);
  const response = await api.get(`/api/google-ads/account-summary?${params}`);
  return response.data;
};

// ==================== FACEBOOK ADS ====================

export const getFacebookAdsStatus = async () => {
  const response = await api.get('/api/facebook-ads/status');
  return response.data;
};

export const getFacebookAdsCampaigns = async (datePreset = 'last_30d', dateFrom = null, dateTo = null) => {
  let url = `/api/facebook-ads/campaigns?date_preset=${datePreset}`;
  if (dateFrom && dateTo) {
    url += `&date_from=${dateFrom}&date_to=${dateTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getFacebookAdsAdsets = async (campaignId, datePreset = 'last_30d', dateFrom = null, dateTo = null) => {
  let url = `/api/facebook-ads/campaigns/${campaignId}/adsets?date_preset=${datePreset}`;
  if (dateFrom && dateTo) {
    url += `&date_from=${dateFrom}&date_to=${dateTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getFacebookAdsAds = async (adsetId, datePreset = 'last_30d', dateFrom = null, dateTo = null) => {
  let url = `/api/facebook-ads/adsets/${adsetId}/ads?date_preset=${datePreset}`;
  if (dateFrom && dateTo) {
    url += `&date_from=${dateFrom}&date_to=${dateTo}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getFacebookAdsAccountSummary = async (datePreset = 'last_30d') => {
  const response = await api.get(`/api/facebook-ads/account-summary?date_preset=${datePreset}`);
  return response.data;
};

// Get ALL adsets from account (optimized)
export const getAllFacebookAdsets = async (datePreset = 'last_30d') => {
  const response = await api.get(`/api/facebook-ads/adsets?date_preset=${datePreset}`);
  return response.data;
};

// Get ALL ads from account (optimized)
export const getAllFacebookAds = async (datePreset = 'last_30d') => {
  const response = await api.get(`/api/facebook-ads/ads?date_preset=${datePreset}`);
  return response.data;
};

// ==================== FACEBOOK LEAD ADS ====================

export const getFacebookLeadForms = async () => {
  const response = await api.get('/api/facebook-leads/forms');
  return response.data;
};

export const getFacebookFormMappings = async () => {
  const response = await api.get('/api/facebook-leads/mappings');
  return response.data;
};

export const saveFacebookFormMapping = async (mapping) => {
  const response = await api.post('/api/facebook-leads/mappings', mapping);
  return response.data;
};

export const deleteFacebookFormMapping = async (formId) => {
  const response = await api.delete(`/api/facebook-leads/mappings/${formId}`);
  return response.data;
};

export const getFacebookWebhookInfo = async () => {
  const response = await api.get('/api/facebook-leads/webhook-info');
  return response.data;
};


// ==================== BACKUP / EXPORT ====================

export const getCollectionsInfo = async () => {
  const response = await api.get('/api/backup/collections-info');
  return response.data;
};

export const exportBackup = async (options) => {
  const response = await api.post('/api/backup/export', options);
  return response.data;
};

export const importBackup = async (backupData) => {
  const response = await api.post('/api/backup/import', backupData);
  return response.data;
};

// ==================== LEAD CAMPAIGNS ====================

export const getLeadCampaigns = async (leadId) => {
  const response = await api.get(`/api/leads/${leadId}/campaigns`);
  return response.data;
};

export const addCampaignToLead = async (leadId, tag) => {
  const response = await api.post(`/api/leads/${leadId}/campaigns`, { tag });
  return response.data;
};

export const removeCampaignFromLead = async (leadId, tag) => {
  const response = await api.delete(`/api/leads/${leadId}/campaigns/${tag}`);
  return response.data;
};

export const getCampaignTagsList = async () => {
  const response = await api.get('/api/campaigns/tags-list');
  return response.data;
};

export const migrateCampaignsFormat = async () => {
  const response = await api.post('/api/migrate/campaigns-format');
  return response.data;
};

// Contract signing
export const signContract = async (data) => {
  const response = await api.post('/api/referidos/contract/sign', data);
  return response.data;
};

export const getContractStatus = async () => {
  const response = await api.get('/api/referidos/contract/status');
  return response.data;
};


// Lead Tags
export const getLeadTags = async () => {
  const response = await api.get('/api/lead-tags');
  return response.data;
};

export const createLeadTag = async (data) => {
  const response = await api.post('/api/lead-tags', data);
  return response.data;
};

export const deleteLeadTag = async (tagId) => {
  const response = await api.delete(`/api/lead-tags/${tagId}`);
  return response.data;
};

export const updateLeadTags = async (leadId, tags) => {
  const response = await api.post(`/api/leads/${leadId}/tags`, { tags });
  return response.data;
};


// WhatsApp Templates

export const getWonDealsDetail = async (page = 1, limit = 20, pipelineId = null, period = null, customFrom = null, customTo = null) => {
  const params = new URLSearchParams({ page, limit });
  if (pipelineId && pipelineId !== 'all') params.append('pipeline_id', pipelineId);
  if (period) params.append('period', period);
  if (customFrom) params.append('custom_from', customFrom);
  if (customTo) params.append('custom_to', customTo);
  const response = await api.get(`/api/deals/won-detail?${params}`);
  return response.data;
};

export const getWhatsAppTemplates = async () => {
  const response = await api.get('/api/whatsapp-templates');
  return response.data;
};

export const saveWhatsAppTemplates = async (templates) => {
  const response = await api.post('/api/whatsapp-templates', { templates });
  return response.data;
};


// Lead Trash
export const trashLead = async (leadId) => {
  const response = await api.post(`/api/leads/${leadId}/trash`);
  return response.data;
};

export const restoreLead = async (leadId) => {
  const response = await api.post(`/api/leads/${leadId}/restore`);
  return response.data;
};

export const bulkTrashLeads = async (leadIds) => {
  const response = await api.post('/api/leads/bulk-trash', { leadIds });
  return response.data;
};

export const getTrashedLeads = async (page = 1, limit = 50) => {
  const response = await api.get(`/api/leads?trashed_only=true&page=${page}&limit=${limit}`);
  return response.data;
};

